import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Calendar as CalIcon, XCircle, Loader2, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * PublicCallback — client-facing page opened from the automatic
 * outreach email sent when a lead's Zoho status becomes "Tentativa de
 * contacto". The URL carries a single-lead token and (optionally) an
 * ``action=reschedule|decline`` query param to pre-select the tab.
 *
 * Design guidelines:
 *   - No login. No internal data (email, notes, address) is exposed.
 *   - Two large actions. Reschedule form is the primary flow (green).
 *     Decline is secondary but not hidden (never funnel the client
 *     into staying interested).
 *   - Confirmation screen after submit — no dead ends.
 */
export default function PublicCallback() {
  const { token } = useParams();
  const [search] = useSearchParams();
  const initialTab = (search.get("action") || "reschedule").toLowerCase();

  const [tab, setTab] = useState(initialTab === "decline" ? "decline" : "reschedule");
  const [loading, setLoading] = useState(true);
  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState("");

  const [prefDay, setPrefDay] = useState("");
  const [prefTime, setPrefTime] = useState("");
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setLoading(true);
    setErr("");
    axios.get(`${API}/public/callback/${token}`)
      .then((r) => {
        setCtx(r.data);
        if (r.data?.already_responded) setSubmitted(true);
      })
      .catch((e) => setErr(e.response?.data?.detail || "Ligação inválida ou expirada"))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const body = { kind: tab };
      if (tab === "reschedule") {
        body.preferred_day = prefDay;
        body.preferred_time = prefTime;
      } else {
        body.reason = reason;
      }
      if (comment) body.comment = comment;
      await axios.post(`${API}/public/callback/${token}`, body);
      setSubmitted(true);
    } catch (e) {
      setErr(e.response?.data?.detail || "Não foi possível registar. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/40 flex items-center justify-center px-6">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (err && !ctx) {
    return (
      <div className="min-h-screen bg-muted/40 flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <XCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="font-display text-xl text-foreground mb-1">Ligação inválida</h1>
          <p className="text-sm text-muted-foreground">{err}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    const isDecline = (ctx?.response?.kind || tab) === "decline";
    return (
      <div className="min-h-screen bg-muted/40 flex items-center justify-center px-6">
        <div className="max-w-md text-center bg-card border border-border rounded-2xl p-10 shadow-sm">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
          <h1 className="font-display text-2xl text-foreground mb-2">
            {isDecline ? "Compreendemos" : "Obrigado!"}
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {isDecline
              ? "Registámos que já não deseja ser contactado. Não voltaremos a incomodá-lo."
              : "A sua disponibilidade foi registada. Iremos contactá-lo(a) na melhor altura possível."}
          </p>
          <p className="text-[11px] text-muted-foreground mt-6">
            Caring — Serviço de Apoio ao Cliente
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/40 py-12 px-6">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-muted-foreground font-semibold mb-2">
            <Sparkles className="w-3 h-3" /> Caring
          </div>
          <h1 className="font-display text-2xl text-foreground" data-testid="callback-title">
            {ctx?.contact_first_name ? `Olá ${ctx.contact_first_name}` : "Bom dia"}
          </h1>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            Tentámos entrar em contacto consigo. Escolha uma das opções para nos ajudar
            a servir melhor:
          </p>
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-2 gap-2 mb-6 bg-card border border-border rounded-full p-1">
          <button
            data-testid="callback-tab-reschedule"
            onClick={() => setTab("reschedule")}
            className={`py-2.5 rounded-full text-sm font-semibold transition-colors ${
              tab === "reschedule"
                ? "bg-emerald-600 text-white"
                : "text-muted-foreground hover:bg-muted/40"
            }`}
          >
            Combinar contacto
          </button>
          <button
            data-testid="callback-tab-decline"
            onClick={() => setTab("decline")}
            className={`py-2.5 rounded-full text-sm font-semibold transition-colors ${
              tab === "decline"
                ? "bg-white/10 text-white"
                : "text-muted-foreground hover:bg-muted/40"
            }`}
          >
            Não tenho interesse
          </button>
        </div>

        {/* Form */}
        <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
          {tab === "reschedule" ? (
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--success))] font-semibold">
                <CalIcon className="w-3 h-3" /> Quando prefere ser contactado?
              </div>
              <label className="block">
                <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-1.5">Dia preferido</div>
                <input
                  data-testid="callback-day"
                  type="date"
                  min={new Date().toISOString().slice(0, 10)}
                  value={prefDay}
                  onChange={(e) => setPrefDay(e.target.value)}
                  className="w-full bg-card border border-border rounded-lg px-3 py-2.5 text-base text-foreground focus:outline-none focus:border-emerald-500"
                />
                <div className="text-[10px] text-muted-foreground mt-1">Escolha o dia que lhe der mais jeito. Iremos ligar durante a janela horária que indicar.</div>
              </label>
              <label className="block">
                <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-1.5">Hora preferida</div>
                <select
                  data-testid="callback-time"
                  value={prefTime}
                  onChange={(e) => setPrefTime(e.target.value)}
                  className="w-full bg-card border border-border rounded-lg px-3 py-2.5 text-base text-foreground focus:outline-none focus:border-emerald-500"
                >
                  <option value="">— Seleccione uma janela horária —</option>
                  <option value="09:00-11:00">Manhã · 09h — 11h</option>
                  <option value="11:00-13:00">Meio-dia · 11h — 13h</option>
                  <option value="14:00-16:00">Tarde · 14h — 16h</option>
                  <option value="16:00-18:00">Fim de tarde · 16h — 18h</option>
                  <option value="18:00-20:00">Início da noite · 18h — 20h</option>
                  <option value="qualquer">Qualquer hora do dia</option>
                </select>
              </label>
              <label className="block">
                <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-1.5">Comentário adicional (opcional)</div>
                <textarea
                  data-testid="callback-comment"
                  rows={3}
                  placeholder="Ex: prefiro que me contactem pelo telefone do meu filho…"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-emerald-500 resize-y"
                />
              </label>
              <button
                data-testid="callback-submit"
                onClick={submit}
                disabled={submitting || (!prefDay.trim() && !prefTime.trim())}
                className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-semibold rounded-full py-3 text-sm"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {submitting ? "A registar…" : "Confirmar disponibilidade"}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-foreground font-semibold">
                Qual o motivo?
              </div>
              <div className="space-y-2" data-testid="callback-reasons">
                {(ctx?.decline_reasons || []).map((r) => (
                  <label
                    key={r}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-colors ${
                      reason === r ? "border-slate-900 bg-muted/40" : "border-border hover:bg-muted/40"
                    }`}
                  >
                    <input
                      type="radio"
                      name="reason"
                      value={r}
                      checked={reason === r}
                      onChange={() => setReason(r)}
                      className="accent-slate-900"
                    />
                    <span className="text-sm text-foreground">{r}</span>
                  </label>
                ))}
              </div>
              <label className="block">
                <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-1.5">
                  Comentário adicional (opcional)
                </div>
                <textarea
                  data-testid="callback-comment"
                  rows={3}
                  placeholder="Se quiser, dê-nos mais contexto…"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-slate-500 resize-y"
                />
              </label>
              <button
                data-testid="callback-submit"
                onClick={submit}
                disabled={submitting || !reason.trim()}
                className="w-full inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/10 disabled:opacity-40 text-white font-semibold rounded-full py-3 text-sm"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                {submitting ? "A registar…" : "Confirmar"}
              </button>
            </div>
          )}
          {err && (
            <div className="mt-3 text-xs text-[hsl(var(--danger))] text-center">{err}</div>
          )}
        </div>

        <p className="text-center text-[11px] text-muted-foreground mt-6 leading-relaxed">
          Caring — Apoio Domiciliário<br />
          Se preferir, responda a este email diretamente. As respostas chegam
          directamente ao Serviço de Apoio ao Cliente.
        </p>
      </div>
    </div>
  );
}
